import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SelectItem } from 'primeng/api';
import { environment } from '../../../environments/environment';
import { PagingResultsModel } from '../../models/pagingResultsModel';
import { deepClone } from '../../helpers/utils';
import { FormApprovalQueueModel, HrEmployeeChangeQueueModel } from '../../models/queueModels';
import { EmployeeReferralModel, EmployeeReferralQueueModel, MyEmployeeReferralModel } from '../../models/employeeReferralModel';

@Injectable({
  providedIn: 'root'
})
export class QueuesService {

  constructor(private http: HttpClient) {
  }

  getFormApprovalFormList(): Observable<SelectItem[]> {
    return this.http.get<SelectItem[]>(`${environment.apiServer.webApiURL}/Queues/FormApprovalFormList`);
  }

  getFormApprovalQueue(filter: any): Observable<PagingResultsModel<FormApprovalQueueModel>> {
    if (!filter) {
      filter = {};
      filter.filters = {};
    }
    filter = deepClone(filter);

    return this.http.post<PagingResultsModel<FormApprovalQueueModel>>(`${environment.apiServer.webApiURL}/Queues/FormApprovalQueue`, filter);
  }

  approveFormSubmission(formSubmissionId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiServer.webApiURL}/Queues/ApproveFormSubmission/${formSubmissionId}`, formSubmissionId);
  }

  getUploadDocApprovalCount(): Observable<number> {
    return this.http.get<number>(`${environment.apiServer.webApiURL}/Queues/GetUploadDocApprovalCount`);
  }

  getFormApprovalCount(): Observable<number> {
    return this.http.get<number>(`${environment.apiServer.webApiURL}/Queues/GetFormApprovalCount`);
  }

  getHrEmployeeChangeQueue(filter: any): Observable<PagingResultsModel<HrEmployeeChangeQueueModel>> {
    if (!filter) {
      filter = {};
      filter.filters = {};
    }
    filter = deepClone(filter);

    return this.http.post<PagingResultsModel<HrEmployeeChangeQueueModel>>(`${environment.apiServer.webApiURL}/Queues/HrEmployeeChangeQueue`, filter);
  }

  getHrEmployeeChangeCount(): Observable<number> {
    return this.http.get<number>(`${environment.apiServer.webApiURL}/Queues/GetHrEmployeeChangeCount`);
  }

  dismissHrEmployeeChange(notificationId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiServer.webApiURL}/Queues/DismissHrEmployeeChange/${notificationId}`, notificationId);
  }

  getEmployeeReferral(referralId: number): Observable<EmployeeReferralModel> {
    return this.http.get<EmployeeReferralModel>(`${environment.apiServer.webApiURL}/Queues/GetEmployeeReferral/${referralId}`);
  }

  saveEmployeeReferral(referral: EmployeeReferralModel): Observable<any> {
    return this.http.post<EmployeeReferralModel>(`${environment.apiServer.webApiURL}/Queues/SaveEmployeeReferral/${referral.id}`, referral);
  }

  getEmployeeReferralList(filter: any): Observable<PagingResultsModel<EmployeeReferralQueueModel>> {
    if (!filter) {
      filter = {};
      filter.filters = {};
    }
    filter = deepClone(filter);

    return this.http.post<PagingResultsModel<EmployeeReferralQueueModel>>(`${environment.apiServer.webApiURL}/Queues/EmployeeReferralQueue`,
      filter)
  }

  createReferralFormSubmission(referralId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiServer.webApiURL}/Queues/CreateReferralFormSubmission/${referralId}/`, { referralId: referralId });
  }

  awardReferralBonus(referralId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiServer.webApiURL}/Queues/AwardReferralBonus/${referralId}/`, { referralId: referralId });
  }

  recalculateReferralBonus(payrollBonusId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiServer.webApiURL}/Queues/RecalculateReferralBonus/${payrollBonusId}/`, { payrollBonusId: payrollBonusId });
  }

  deleteReferralBonus(payrollBonusId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiServer.webApiURL}/Queues/DeleteReferralBonus/${payrollBonusId}/`, { payrollBonusId: payrollBonusId });
  }

  toggleArchiveEmployeeReferral(referralId: number, reason: string): Observable<any> {
    const params = new HttpParams()
      .set('referralId', referralId.toString())
      .set('reason', reason);
    return this.http.post<any>(`${environment.apiServer.webApiURL}/Queues/ToggleArchiveReferral/${referralId}/`, null, { params });
  }

  deleteEmployeeReferral(referralId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiServer.webApiURL}/Queues/DeleteReferral/${referralId}/`, { referralId: referralId });
  }

  getMyEmployeeReferralList(filter: any): Observable<PagingResultsModel<MyEmployeeReferralModel>> {
    if (!filter) {
      filter = {};
      filter.filters = {};
    }
    filter = deepClone(filter);

    return this.http.post<PagingResultsModel<MyEmployeeReferralModel>>(`${environment.apiServer.webApiURL}/Queues/MyReferrals`,
      filter)
  }

  rejectEmployeeReferral(referralId: number, reason: string): Observable<any> {
    const params = new HttpParams()
      .set('referralId', referralId.toString())
      .set('reason', reason);
    return this.http.post<any>(`${environment.apiServer.webApiURL}/Queues/RejectEmployeeReferral/${referralId}`, null, { params });
  }

  resetEmployeeReferral(referralId: number): Observable<any> {
    return this.http.post<any>(`${environment.apiServer.webApiURL}/Queues/ResetEmployeeReferral/${referralId}/`, { referralId: referralId });
  }

}
